import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryClient } from 'App'
import { AxiosError } from 'axios'
import { EMPLOYEES_KEY, getMembersKey, REQUESTED_EMPLOYEE_KEY } from 'common/constants/queryKeys'
import { removeElementById } from 'common/utils/array'
import { UserInfoResponse } from 'modules/user/api/types'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import {
  acceptEmployee,
  createEmployee,
  createMembers,
  createRoles,
  deleteMembers,
  followOwner,
  rejectEmployee,
  removeEmployee,
  removeRole,
  resetPassword,
  updateEmployeeRole,
  updateMembers,
  updateRole,
} from '.'
import { useGetEmployees, useGetMembers } from './queries'
import { useRoles } from './selector'
import { EmployeeData } from './types'

export const useMutateAcceptEmployee = () => {
  return useMutation(acceptEmployee, {
    onSuccess: (res, employeeId) => {
      const { status } = res.data
      if (status !== 200) return

      const requestedEmployees =
        queryClient.getQueryData<UserInfoResponse[]>(REQUESTED_EMPLOYEE_KEY)

      if (requestedEmployees) {
        const updatedRequestedEmployees = removeElementById(requestedEmployees, employeeId)
        queryClient.setQueryData(REQUESTED_EMPLOYEE_KEY, updatedRequestedEmployees)
      }
    },
  })
}

export const useMutateRejectEmployee = () => {
  return useMutation(rejectEmployee, {
    onSuccess: (res, employeeId) => {
      const { status } = res.data
      if (status !== 200) return

      const requestedEmployees =
        queryClient.getQueryData<UserInfoResponse[]>(REQUESTED_EMPLOYEE_KEY)

      const updatedRequestedEmployees = removeElementById(requestedEmployees, employeeId)
      queryClient.setQueryData(REQUESTED_EMPLOYEE_KEY, updatedRequestedEmployees)
    },
  })
}

export const useMutateFollowOwner = () => {
  return useMutation(followOwner)
}

export const useMutateUpdateEmployeeRole = () => {
  return useMutation(updateEmployeeRole, {
    onSuccess: (res, variables) => {
      const { status } = res.data
      if (status !== 200) return

      queryClient.setQueryData<InfiniteData<EmployeeData>>(EMPLOYEES_KEY, (data) => {
        if (!data) return data
        return {
          pageParams: data.pageParams,
          pages: data.pages.map((page) => ({
            ...page,
            employees: page.employees.map((el: UserInfoResponse) =>
              el.id === variables.employeeId ? { ...el, role: variables.role } : el
            ),
          })),
        }
      })
    },
  })
}

export const useMutateRemoveEmployee = () => {
  return useMutation(removeEmployee, {
    onSuccess: (res, employeeId) => {
      const { status } = res.data
      if (status !== 200) return

      queryClient.setQueryData<InfiniteData<EmployeeData>>(EMPLOYEES_KEY, (data) => {
        if (!data) return data
        return {
          pageParams: data.pageParams,
          pages: data.pages.map((page) => ({
            ...page,
            employees: page.employees.filter((el: UserInfoResponse) => el.id !== employeeId),
          })),
        }
      })
    },
  })
}

// v2

export const useMutateCreateEmployee = () => {
  const { refetch } = useGetEmployees()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['employee'])

  return useMutation(createEmployee, {
    onSuccess: (res) => {
      const { status } = res
      if (status === 200) {
        enqueueSnackbar(t('create.employee.success'), { variant: 'success' })
        refetch()
      }
    },
    onError: (err) => {
      console.error(err)
    },
  })
}

//v3

export const useMutateCreateMembers = () => {
  const { refetch } = useGetMembers()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(createMembers, {
    onSuccess: (res) => {
      const { status } = res
      if (status === 201) {
        enqueueSnackbar(t('common:save.success'), { variant: 'success' })
        refetch()
      }
    },
    onError: (err) => {
      console.error(err)
    },
  })
}

export const useMutateUpdateMembers = () => {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])
  const queryClient = useQueryClient()

  return useMutation(updateMembers, {
    onSuccess: (res) => {
      const { status } = res
      if (status === 200) {
        enqueueSnackbar(t('common:save.success'), { variant: 'success' })
        queryClient.invalidateQueries(getMembersKey.all())
      }
    },
    onError: (err) => {
      console.error(err)
    },
  })
}

export const useMutateDeleteMembers = () => {
  const { refetch } = useGetMembers()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(deleteMembers, {
    onSuccess: (res) => {
      const { status } = res
      if (status === 200) {
        enqueueSnackbar(t('common:delete.success'), { variant: 'success' })
        refetch()
      }
    },
    onError: (err) => {
      console.error(err)
    },
  })
}

export const useMutateCreateRoles = () => {
  const { refetch } = useRoles()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(createRoles, {
    onSuccess: (res) => {
      const { status } = res
      if (status === 200) {
        enqueueSnackbar(t('common:save.success'), { variant: 'success' })
        refetch()
      }
    },
    onError: (err) => {
      console.error(err)
    },
  })
}

export const useMutateUpdateRoles = () => {
  const { refetch } = useRoles()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(updateRole, {
    onSuccess: (res) => {
      const { status } = res
      if (status === 200) {
        enqueueSnackbar(t('common:edit.success'), { variant: 'success' })
        refetch()
      }
    },
    onError: (err) => {
      console.error(err)
    },
  })
}

export const useMutateDeleteRole = () => {
  const { refetch } = useRoles()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(removeRole, {
    onSuccess: (res) => {
      const { status } = res
      if (status === 200) {
        enqueueSnackbar(t('common:delete.success'), { variant: 'success' })
        refetch()
      }
    },
    onError: (err: AxiosError) => {
      console.error(err)
      if (err?.response?.status === 409) {
        enqueueSnackbar(t('member:delete.error.role.in_use'), { variant: 'error' })
      }
    },
  })
}

export const useMutateResetMemberPassword = () => {
  const { refetch } = useRoles()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation(['common'])

  return useMutation(resetPassword, {
    onSuccess: (res) => {
      const { status } = res
      if (status === 200) {
        enqueueSnackbar(t('common:password.success'), { variant: 'success' })
        refetch()
      }
    },
    onError: (err) => {
      console.error(err)
    },
  })
}
